import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { searchGrants } from '../api'
import { getClientId } from '../session'
import Button from '../components/Button'
import GrantCard from '../components/GrantCard'
import { ErrorAlert } from '../components/Alert'
import { FOCUS_AREA_OPTIONS, US_STATES } from '../options'
import { useDocumentTitle } from '../components/PublicLayout'

// The source filter was removed 2026-07-27 (homelab-kb #40). It offered three
// options and none of them worked for a user:
//
//   sam_gov        retired 2026-07-14 (procurement contracts, not grants) —
//                  0 rows, so picking it silently returned nothing and the
//                  empty state advised "try a broader keyword", which could
//                  never help
//   grants_gov     1,236 rows / 855 open — the legacy ingest
//   simpler_grants 1,836 rows / 1,243 open — the live ingest (ADR-0005)
//
// The last two are the same federal grants arriving down two pipelines, so
// the control asked the user to understand our ingestion history to filter
// their own search, and once the dead option goes there is nothing left for
// it to discriminate. The backend `source` param still works for API callers.

const DEBOUNCE_MS = 350

const EMPTY_FILTERS = {
  q: '', category: '', state: '', status: 'open', sort: 'newest',
}

/**
 * Federal grant search (Phase 7F) — auth-gated search over the WHOLE federal
 * grants corpus, not just precomputed matches. Every result carries a live
 * personalized match score; grants that are also real matches keep their
 * feedback thumbs.
 *
 * Lives at /grants/federal under the "Grant Database" nav menu. This page
 * covers FEDERAL programs only: real opportunities with deadlines and CFDA
 * numbers. Foundations are a separate surface and a different kind of thing
 * (leads derived from 990 filings, no RFP, no opportunity deadline), so they
 * get their own page rather than a filter here.
 */
export default function GrantSearch() {
  useDocumentTitle('Federal grants')
  const navigate = useNavigate()
  const clientId = getClientId()

  const [filters, setFilters] = useState(EMPTY_FILTERS)
  const [page, setPage] = useState(1)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  // Monotonic id so a slow earlier response can never clobber a newer one.
  const requestId = useRef(0)

  useEffect(() => {
    if (!clientId) { navigate('/signin'); return }
    const id = ++requestId.current
    setLoading(true)
    const timer = setTimeout(() => {
      searchGrants({ ...filters, page })
        .then(res => {
          if (requestId.current !== id) return
          setData(res)
          setError('')
        })
        .catch(err => {
          if (requestId.current !== id) return
          if (!getClientId()) { navigate('/signin'); return }
          setError(err.message)
        })
        .finally(() => {
          if (requestId.current === id) setLoading(false)
        })
    }, DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [clientId, filters, page])

  function setFilter(key, value) {
    setFilters(f => ({ ...f, [key]: value }))
    setPage(1)
  }

  const results = data?.results || []
  const total = data?.total ?? 0
  const pageSize = data?.page_size || 20
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const hasFilters =
    filters.q || filters.category || filters.state ||
    filters.status !== 'open' || filters.sort !== 'newest'

  const selectClass =
    'rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 ' +
    'focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500'

  return (
    <div className="max-w-3xl mx-auto px-4 py-10 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Federal Grants</h1>
        <p className="text-gray-500 text-sm mt-1">
          Search every federal grant program we track. Each result is scored
          against your organization's profile.
        </p>
      </div>

      <ErrorAlert message={error} />

      {/* Search + filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
        <input
          type="search"
          value={filters.q}
          onChange={e => setFilter('q', e.target.value)}
          placeholder="Search by keyword, agency, or opportunity number…"
          className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm
                     focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
        />
        <div className="flex flex-wrap gap-2">
          <select
            value={filters.category}
            onChange={e => setFilter('category', e.target.value)}
            className={selectClass}
            aria-label="Category"
          >
            <option value="">All categories</option>
            {FOCUS_AREA_OPTIONS.map(({ code, label }) => (
              <option key={code} value={code}>{label}</option>
            ))}
          </select>
          <select
            value={filters.state}
            onChange={e => setFilter('state', e.target.value)}
            className={selectClass}
            aria-label="State"
          >
            <option value="">All states</option>
            {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select
            value={filters.status}
            onChange={e => setFilter('status', e.target.value)}
            className={selectClass}
            aria-label="Status"
          >
            <option value="open">Open</option>
            <option value="closed">Closed</option>
            <option value="all">All statuses</option>
          </select>
          <select
            value={filters.sort}
            onChange={e => setFilter('sort', e.target.value)}
            className={selectClass}
            aria-label="Sort"
          >
            <option value="newest">Newest first</option>
            <option value="closing_soon">Closing soon</option>
          </select>
          {hasFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setFilters(EMPTY_FILTERS); setPage(1) }}
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Results */}
      <div aria-busy={loading}>
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm text-gray-500">
            {loading && data === null
              ? 'Searching…'
              : `${total.toLocaleString()} grant${total === 1 ? '' : 's'} found`}
          </p>
          {results.length > 0 && (
            <p className="text-xs text-gray-500">
              Click a grant to see why it fits your profile.
            </p>
          )}
        </div>

        {results.length > 0 ? (
          <div className={`space-y-2 ${loading ? 'opacity-60' : ''}`}>
            {results.map(grant => (
              <GrantCard key={grant.grant_id} grant={grant} clientId={clientId} />
            ))}
          </div>
        ) : loading ? (
          <div className="space-y-2 animate-pulse">
            {[0, 1, 2, 3, 4].map(i => (
              <div key={i} className="bg-white rounded-lg border border-gray-200 flex items-center gap-3 px-4 py-3">
                <div className="h-8 w-10 bg-gray-200 rounded flex-shrink-0" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-4 w-3/4 bg-gray-200 rounded" />
                  <div className="h-3 w-1/2 bg-gray-100 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-dashed border-gray-300 p-10 text-center">
            <div className="text-4xl mb-3">🔍</div>
            <h3 className="font-semibold text-gray-900 mb-1">No grants found</h3>
            <p className="text-sm text-gray-500 max-w-sm mx-auto">
              Try a broader keyword, another category, or switch the status
              filter to "All statuses".
            </p>
          </div>
        )}

        {/* Pagination */}
        {total > pageSize && (
          <div className="mt-5 flex items-center justify-center gap-4">
            <Button
              variant="secondary"
              size="sm"
              disabled={page <= 1 || loading}
              onClick={() => setPage(p => p - 1)}
            >
              ← Previous
            </Button>
            <span className="text-sm text-gray-500">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="secondary"
              size="sm"
              disabled={page >= totalPages || loading}
              onClick={() => setPage(p => p + 1)}
            >
              Next →
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
